import { useEffect, useState } from "react";
import { http } from "../api";
import { ClipboardList } from "lucide-react";
import { Modal, PageHeader, AddButton, FormRow, EmptyState } from "../shared";

const empty = { title: "", description: "", owner: "Unassigned", due_date: "", priority: "Medium", status: "Open", category: "General", points: 5 };
const statuses = ["Open", "In Progress", "Done"];
const priorities = ["Urgent", "High", "Medium", "Low"];
function pTone(p) { return { Urgent: "red", High: "amber", Medium: "blue", Low: "green" }[p] || "blue"; }
function sTone(s) { return { Open: "amber", "In Progress": "blue", Done: "green" }[s] || "blue"; }

export default function Tasks() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => http.get(filter ? `/tasks?status=${filter}` : "/tasks").then((r) => setRows(r.data));
  useEffect(() => { load(); }, [filter]);

  const save = async () => {
    setSaving(true);
    try { await http.post("/tasks", { ...form, points: Number(form.points) }); setShow(false); setForm(empty); load(); }
    finally { setSaving(false); }
  };

  const moveStatus = async (t, next) => {
    await http.patch(`/tasks/${t.task_id}`, { ...t, status: next });
    load();
  };

  const columns = ["Open", "In Progress", "Done"];

  return (
    <>
      <PageHeader eyebrow="FIELDWORK QUEUE" title="Tasks" subtitle="Assign and track team work with points"
        actions={<AddButton onClick={() => setShow(true)} label="Add task" testid="tasks-add-button" />} />

      <div className="chip-row">
        <button className={`chip-filter ${!filter ? "active" : ""}`} data-testid="filter-all" onClick={() => setFilter("")}>All</button>
        {statuses.map((s) => (
          <button key={s} className={`chip-filter ${filter === s ? "active" : ""}`} data-testid={`filter-${s.toLowerCase().replace(" ", "-")}`} onClick={() => setFilter(s)}>{s}</button>
        ))}
      </div>

      <div className="kanban">
        {columns.map((col) => (
          <div key={col} className="kanban-col" data-testid={`task-column-${col.toLowerCase().replace(" ", "-")}`}>
            <div className="kanban-head"><b>{col}</b><small>{rows.filter((t) => t.status === col).length}</small></div>
            {rows.filter((t) => t.status === col).map((t, i) => (
              <div key={t.task_id} className="task-card" data-testid={`task-card-${t.task_id}`}>
                <div className="task-head">
                  <span className={`chip ${pTone(t.priority)}`}>{t.priority}</span>
                  <small>+{t.points} pts</small>
                </div>
                <b>{t.title}</b>
                <p className="hint">{t.description}</p>
                <div className="task-foot">
                  <small>{t.owner} · Due {t.due_date || "—"}</small>
                  <div className="task-move">
                    {columns.filter((c) => c !== col).map((c) => (
                      <button key={c} className="link" data-testid={`move-task-${t.task_id}-${c.toLowerCase().replace(" ", "-")}`} onClick={() => moveStatus(t, c)}>→ {c}</button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {rows.filter((t) => t.status === col).length === 0 && <p className="hint">Nothing here.</p>}
          </div>
        ))}
      </div>

      {show && (
        <Modal title="Add task" onClose={() => setShow(false)} testid="task-modal">
          <FormRow label="Title"><input data-testid="form-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormRow>
          <FormRow label="Description"><input data-testid="form-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
          <div className="form-grid">
            <FormRow label="Owner"><input data-testid="form-owner-input" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></FormRow>
            <FormRow label="Due"><input data-testid="form-due-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></FormRow>
            <FormRow label="Priority"><select data-testid="form-priority-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>{priorities.map((p) => <option key={p}>{p}</option>)}</select></FormRow>
            <FormRow label="Points"><input data-testid="form-points-input" type="number" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} /></FormRow>
          </div>
          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => setShow(false)}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !form.title} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </Modal>
      )}
    </>
  );
}
